import type DxfArrayScanner from '../DxfArrayScanner';
import { serializeGroupValue } from '../DxfArrayScanner';
import {
  checkCommonEntityProperties,
  parsePoint,
  serializeCommonEntityProperty,
  serializePoint,
} from '../ParseHelpers';
import type IGeometry from './geometry';
import type { IEntity, IPoint } from './geometry';
import { EntityName } from './geometry';

type IVertex = IPoint & {
  //direction and miterDirection are specified for each segment (each vertex after first)
  direction?: IPoint; //12, 22, 32
  miterDirection?: IPoint; //13, 23, 33
  parameters?: number[][]; //41
  areaFillParameters?: number[][]; //42
};

export type IMLineEntity = IEntity & {
  type: EntityName.MLine;
  vertices: IVertex[];
  extrusionDirection: IPoint; //210
  mlineStyle: string; //2
  scale?: number; //40
  justificationFlags?: number; //70
  flags?: number; //71
  verticesCount?: number; //72
  styleElementsCount?: number; //73
};

const serializeMLineVertices = function* (
  vertices: IVertex[],
  code: number,
): IterableIterator<string> {
  for (const vertex of vertices) {
    for (const [property, value] of Object.entries(vertex) as [
      keyof IVertex,
      IVertex[keyof IVertex],
    ][])
      switch (property) {
        case 'x':
          yield* serializePoint(vertex, code);
          break;
        case 'y':
        case 'z':
          break; //already handled by serializePoint()
        case 'direction':
          yield* serializePoint(value as IPoint, 11);
          break;
        case 'miterDirection':
          yield* serializePoint(value as IPoint, 13);
          break;
        case 'parameters':
          for (const ps of value as number[][]) {
            yield* serializeGroupValue(74, ps.length);
            for (const p of ps) {
              yield* serializeGroupValue(41, p);
            }
          }
          break;
        case 'areaFillParameters':
          for (const ps of value as number[][]) {
            yield* serializeGroupValue(75, ps.length);
            for (const p of ps) {
              yield* serializeGroupValue(42, p);
            }
          }
          break;
      }
  }
};

const mlinePropertyFromCode = new Map<number, keyof IMLineEntity>([
  [2, 'mlineStyle'],
  [40, 'scale'],
  [70, 'justificationFlags'],
  [71, 'flags'],
  [72, 'verticesCount'],
  [73, 'styleElementsCount'],
]);

const codeFromMlineProperty = new Map<keyof IMLineEntity, number>(
  Array.from(mlinePropertyFromCode.entries()).map(([code, property]) => [
    property,
    code,
  ]),
);

export default class MLine implements IGeometry<IMLineEntity> {
  public ForEntityName = EntityName.MLine;
  public parseEntity(scanner: DxfArrayScanner): IMLineEntity {
    const entity = { type: this.ForEntityName } as IMLineEntity;
    let curr = scanner.next();
    let vertex: IVertex | undefined;
    const vertices: IVertex[] = [];
    const directions: IPoint[] = [];
    const miterDirections: IPoint[] = [];
    const parameterss: number[][][] = [];
    let parameters: number[][] | undefined;
    let ps: number[] = [];
    const areaFillParameterss: number[][][] = [];
    let areaFillParameters: number[][] | undefined;
    let aPs: number[] = [];

    while (!scanner.isEOF()) {
      if (curr.code === 0) break;

      const property = mlinePropertyFromCode.get(curr.code);
      if (property != null) {
        (entity[property] as string | number) = curr.value as string | number;
      } else {
        //special cases
        switch (curr.code) {
          case 10: // X
          case 11: // X
            entity.vertices = vertices;
            if (!vertex || 'x' in vertex) {
              vertex = {} as IVertex;
              vertices.push(vertex);
              parameters = [];
              parameterss.push(parameters);
              areaFillParameters = [];
              areaFillParameterss.push(areaFillParameters);
            }
            vertex.x = curr.value as number;
            break;
          case 20: // Y
          case 21: // Y
            if (!vertex || 'y' in vertex) {
              vertex = {} as IVertex;
              vertices.push(vertex);
            }
            vertex.y = curr.value as number;
            break;
          case 30: // Z
          case 31: // Z
            if (!vertex || 'z' in vertex) {
              vertex = {} as IVertex;
              vertices.push(vertex);
            }
            vertex.z = curr.value as number;
            if (curr.code === 30) {
              vertex = {} as IVertex; //done with first point
            }
            break;
          case 12:
            directions.push(parsePoint(scanner));
            break;
          case 13:
            miterDirections.push(parsePoint(scanner));
            break;
          case 74:
            if (parameters) {
              ps = [];
              parameters.push(ps);
            }
            break;
          case 41:
            ps.push(curr.value as number);
            break;
          case 75:
            if (areaFillParameters) {
              aPs = [];
              areaFillParameters.push(aPs);
            }
            break;
          case 42:
            aPs.push(curr.value as number);
            break;
          case 210:
            entity.extrusionDirection = parsePoint(scanner);
            break;
          default:
            checkCommonEntityProperties(entity, curr, scanner);
            break;
        }
      }

      curr = scanner.next();
    }
    if (directions.length === entity.vertices.length) {
      for (const [i, d] of directions.entries()) {
        entity.vertices[i].direction = d;
      }
    }
    if (miterDirections.length === entity.vertices.length) {
      for (const [i, d] of miterDirections.entries()) {
        entity.vertices[i].miterDirection = d;
      }
    }
    if (parameterss.length === entity.vertices.length) {
      for (const [i, parameters] of parameterss.entries()) {
        entity.vertices[i].parameters = parameters;
      }
    }
    if (areaFillParameterss.length === entity.vertices.length) {
      for (const [i, areaFillParameters] of areaFillParameterss.entries()) {
        entity.vertices[i].areaFillParameters = areaFillParameters;
      }
    }

    return entity;
  }

  public *serializeEntity(entity: IMLineEntity): IterableIterator<string> {
    for (const [property, value] of Object.entries(entity) as [
      keyof IMLineEntity,
      IMLineEntity[keyof IMLineEntity],
    ][]) {
      const code = codeFromMlineProperty.get(property);
      if (code != null) {
        yield* serializeGroupValue(code, value as string | number | boolean);
      } else {
        switch (property) {
          case 'vertices':
            yield* serializeMLineVertices(value as IVertex[], 10);
            break;
          case 'extrusionDirection':
            yield* serializePoint(value as IPoint, 210);
            break;
          default:
            yield* serializeCommonEntityProperty(
              property,
              value as string | number | boolean,
              entity,
            );
            break;
        }
      }
    }
  }
}
